"use client"

import { useMutation } from "@tanstack/react-query"

import { useAuth } from "@/hooks/use-auth"
import { useTranslatedQueryHandling } from "@/hooks/use-translated-query-handling"


export type UploadKnowledgeBaseDocumentInput = {
  file: File
  title?: string
  category?: string
  tags?: string[]
  injectionMode?: "auto" | "direct" | "rag"
  isDefault?: boolean
  folderId?: string
  showNotifications?: boolean
}

export type UploadKnowledgeBaseDocumentResult = {
  document: {
    id: string
  }
  parsed: {
    wordCount: number
    charCount: number
    pageCount?: number
    tokenCount: number
  }
  agents_updated?: number
}

export function useUploadKnowledgeBaseDocumentMutation(options?: {
  onSuccess?: (
    result: UploadKnowledgeBaseDocumentResult,
    input: UploadKnowledgeBaseDocumentInput,
  ) => void | Promise<void>
}) {
  const withQueryHandling = useTranslatedQueryHandling()

  const { fetchWithAuth } = useAuth()

  return useMutation({
    mutationFn: async (
      input: UploadKnowledgeBaseDocumentInput,
    ): Promise<UploadKnowledgeBaseDocumentResult> => {
      const request = uploadKnowledgeBaseDocument(fetchWithAuth, input)
      if (input.showNotifications === false) return request

      return withQueryHandling(request, {
        loading: `Uploading ${input.file.name}...`,
        success: (result) =>
          `Created ${input.file.name} (${result.parsed.wordCount} words)`,
      })
    },
    onSuccess: options?.onSuccess,
  })
}

async function uploadKnowledgeBaseDocument(
  fetchWithAuth: (
    url: string,
    options?: RequestInit,
  ) => Promise<Response>,
  input: UploadKnowledgeBaseDocumentInput,
): Promise<UploadKnowledgeBaseDocumentResult> {
  const formData = new FormData()
  formData.append("file", input.file)
  formData.append(
    "title",
    input.title?.trim() || input.file.name.replace(/\.[^/.]+$/, ""),
  )
  if (input.category) formData.append("category", input.category)
  if (input.tags?.length) formData.append("tags", input.tags.join(","))
  if (input.injectionMode) {
    formData.append("injection_mode", input.injectionMode)
  }
  if (input.isDefault) formData.append("is_default", "true")
  if (input.folderId) formData.append("folder_id", input.folderId)

  const response = await fetchWithAuth(
    "/api/ai/knowledge-base/documents/upload",
    {
      method: "POST",
      body: formData,
    },
  )
  if (!response.ok) {
    const error = (await response.json().catch(() => ({}))) as {
      error?: string
    }
    throw new Error(error.error || "Failed to upload file")
  }

  return response.json() as Promise<UploadKnowledgeBaseDocumentResult>
}
